import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bot, CalendarDays, CheckCircle2, Crown, Film, Layers3, Lock, Play, Shield, Sparkles, Wand2
} from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  agent_studio: Bot,
  content_lab: Sparkles,
  social_calendar: CalendarDays,
  video_studio: Film,
  auto_publishing: Play,
  workspace_analytics: Layers3,
  multibrand_ops: Crown,
  partner_network: Shield,
  executive_control_tower: Crown,
};

const LAUNCH_STATUS_STYLES = {
  borrador: 'bg-amber-500/15 text-amber-300 border-amber-400/20',
  en_proceso: 'bg-sky-500/15 text-sky-300 border-sky-400/20',
  listo: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20',
};

const APPROVAL_STYLES = {
  pendiente: 'bg-amber-500/15 text-amber-300 border-amber-400/20',
  aprobado: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20',
  rechazado: 'bg-rose-500/15 text-rose-300 border-rose-400/20',
};

const formatLaunchLabel = (value) => (value || 'pendiente').replaceAll('_', ' ');

export default function WorkspaceHub({ authFetch, isAdminPreview = false }) {
  const actionBuilderRef = useRef(null);
  const [workspace, setWorkspace] = useState(null);
  const [previewPlan, setPreviewPlan] = useState('neuralia_holding');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeAction, setActiveAction] = useState('content');
  const [actionResult, setActionResult] = useState(null);
  const [campaignForm, setCampaignForm] = useState({ campaign_name: '', business_goal: '', audience: '', brand_voice: 'Cercano, experto y claro', offers: '', platforms: 'instagram,facebook,youtube', weeks: 4, posts_per_week: 3 });
  const [videoForm, setVideoForm] = useState({ title: '', objective: '', audience: '', primary_platform: 'tiktok', duration_seconds: 45 });
  const [publishForm, setPublishForm] = useState({ campaign_name: '', channels: 'instagram,facebook', primary_message: '', scheduled_for: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_agents: 'CONTENIDO,VENTAS', priority: 'media' });

  const activateAction = (actionId) => {
    setActiveAction(actionId);
    window.setTimeout(() => {
      actionBuilderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const loadWorkspace = useCallback(async (plan = previewPlan) => {
    try {
      setLoading(true);
      const query = isAdminPreview ? `?preview_plan=${plan}` : '';
      const res = await authFetch(`${API}/workspace/overview${query}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el workspace');
      setWorkspace(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el workspace');
    } finally {
      setLoading(false);
    }
  }, [authFetch, isAdminPreview, previewPlan]);

  useEffect(() => {
    loadWorkspace(previewPlan);
  }, [loadWorkspace, previewPlan]);

  const securityHighlights = useMemo(() => workspace?.security?.highlights?.slice(0, 4) || [], [workspace?.security?.highlights]);
  const launchPack = workspace?.launch_pack;

  const applyLaunchPrefill = (actionId) => {
    if (!launchPack) return;
    if (actionId === 'content') {
      setCampaignForm((prev) => ({ ...prev, ...launchPack.prefill_campaign }));
    }
    if (actionId === 'publish') {
      setPublishForm((prev) => ({ ...prev, ...launchPack.prefill_publish }));
    }
    activateAction(actionId);
    toast.success(actionId === 'publish' ? 'Pack cargado para preparar publicación' : 'Pack cargado en el editor');
  };

  const submitWorkspaceAction = async () => {
    try {
      setSubmitting(true);
      setActionResult(null);
      let endpoint = '';
      let payload = {};

      if (activeAction === 'content') {
        endpoint = `${API}/workspace/campaigns`;
        payload = {
          ...campaignForm,
          platforms: campaignForm.platforms.split(',').map((item) => item.trim()).filter(Boolean),
          weeks: Number(campaignForm.weeks),
          posts_per_week: Number(campaignForm.posts_per_week),
        };
      }
      if (activeAction === 'video') {
        endpoint = `${API}/workspace/videos`;
        payload = { ...videoForm, duration_seconds: Number(videoForm.duration_seconds) };
      }
      if (activeAction === 'publish') {
        endpoint = `${API}/workspace/publish-queue`;
        payload = {
          ...publishForm,
          channels: publishForm.channels.split(',').map((item) => item.trim()).filter(Boolean),
        };
      }
      if (activeAction === 'agents') {
        endpoint = `${API}/workspace/agent-tasks`;
        payload = {
          ...taskForm,
          assigned_agents: taskForm.assigned_agents.split(',').map((item) => item.trim()).filter(Boolean),
          auto_execute: true,
        };
      }

      const res = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo ejecutar la acción');
      setActionResult(data);
      toast.success('Acción del Workspace creada correctamente');
      await loadWorkspace(previewPlan);
    } catch (error) {
      toast.error(error.message || 'No se pudo ejecutar la acción');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !workspace) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="workspace-loading">Cargando Neuralia Workspace...</div>;
  }

  if (!workspace) return null;

  return (
    <div className="space-y-6" data-testid="workspace-hub">
      <div className="rounded-[30px] border border-primary/15 bg-[radial-gradient(circle_at_top_left,rgba(0,229,255,0.18),transparent_40%),linear-gradient(135deg,rgba(7,16,40,0.96),rgba(3,8,22,0.96))] p-6 lg:p-8" data-testid="workspace-hero">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2" data-testid="workspace-plan-chip">
              <Wand2 className="h-4 w-4 text-primary" />
              <span className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                {workspace.access.is_admin_preview ? `Vista previa admin · ${workspace.plan.name}` : workspace.access.has_access ? workspace.plan.name : 'Workspace bloqueado'}
              </span>
            </div>
            <h3 className="mt-5 text-3xl lg:text-4xl font-semibold text-text-heading">Neuralia Workspace</h3>
            <p className="mt-3 text-sm lg:text-base leading-relaxed text-text-body" data-testid="workspace-hero-description">
              {workspace.access.has_access
                ? `Tu cabina para activar agentes, coordinar unidades, producir contenido y operar con la capa Director IA Neuralia según el nivel contratado.`
                : 'Este panel está preparado para clientes con acceso a Director IA Neuralia. Activa Core Ops, Growth OS, Group OS o Holding Command para desbloquear módulos y capacidad operativa.'}
            </p>
          </div>

          {isAdminPreview && (
            <div className="rounded-[24px] border border-border-subtle bg-background/55 p-4 min-w-[280px]" data-testid="workspace-admin-preview-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Preview plan</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  ['neuralia_creator', 'Core Ops'],
                  ['neuralia_growth', 'Growth OS'],
                  ['neuralia_scale', 'Group OS'],
                  ['neuralia_holding', 'Holding'],
                ].map(([plan, label]) => (
                  <button key={plan} onClick={() => setPreviewPlan(plan)} className={`rounded-full px-3 py-2 text-xs border ${previewPlan === plan ? 'bg-primary text-background border-primary' : 'border-border-subtle text-text-body bg-card/40'}`} data-testid={`workspace-preview-${plan}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {workspace.stats.map((item) => (
          <div key={item.id} className="glass-card p-5" data-testid={`workspace-stat-${item.id}`}>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{item.label}</p>
            <p className="mt-3 text-3xl font-bold text-text-heading">{item.value}</p>
            <p className="mt-2 text-xs text-text-muted">{item.helper}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="glass-card p-6" data-testid="workspace-plan-services">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Servicios incluidos</p>
          <h4 className="mt-2 text-xl font-semibold text-text-heading">{workspace.plan.name}</h4>
          <p className="mt-2 text-sm text-text-body">{workspace.plan.tagline}</p>
          <div className="mt-5 space-y-3">
            {workspace.plan.features.map((feature) => (
              <div key={feature} className="flex items-start gap-2 text-sm text-text-body" data-testid="workspace-plan-feature-row">
                <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="workspace-quick-actions">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Quick start</p>
          <h4 className="mt-2 text-xl font-semibold text-text-heading">Acciones rápidas</h4>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {workspace.quick_actions.map((action) => (
              <button key={action.id} onClick={() => action.enabled && activateAction(action.id)} className={`rounded-[22px] border p-4 text-left transition-colors ${action.enabled ? activeAction === action.id ? 'border-primary bg-primary/10' : 'border-primary/20 bg-primary/5 hover:border-primary/40' : 'border-border-subtle bg-card/40 opacity-70 cursor-not-allowed'}`} disabled={!action.enabled} data-testid={`workspace-action-${action.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-base font-semibold text-text-heading">{action.title}</p>
                  {action.enabled ? <Sparkles className="w-4 h-4 text-primary" /> : <Lock className="w-4 h-4 text-text-muted" />}
                </div>
                <p className="mt-2 text-sm text-text-body">{action.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {launchPack && (
        <div className="glass-card p-6" data-testid="workspace-launch-pack-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Pack destacado</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading" data-testid="workspace-launch-pack-title">{launchPack.campaign_name}</h4>
              <p className="mt-2 text-sm text-text-body" data-testid="workspace-launch-pack-description">
                El pack de lanzamiento ya tiene estrategia comercial y ángulos de venta listos. Lo he dejado visible aquí para revisarlo, afinarlo y reutilizarlo mañana al conectar Google y redes.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${LAUNCH_STATUS_STYLES[launchPack.status] || 'bg-card text-text-muted border-border-subtle'}`} data-testid="workspace-launch-pack-status">
                  Estado · {formatLaunchLabel(launchPack.status)}
                </span>
                <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${APPROVAL_STYLES[launchPack.approval_status] || 'bg-card text-text-muted border-border-subtle'}`} data-testid="workspace-launch-pack-approval">
                  Aprobación · {formatLaunchLabel(launchPack.approval_status)}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button className="btn-secondary text-sm" onClick={() => applyLaunchPrefill('content')} data-testid="workspace-launch-prefill-campaign-button">
                <Sparkles className="w-4 h-4" /> Cargar campaña
              </button>
              <button className="btn-primary text-sm" onClick={() => applyLaunchPrefill('publish')} data-testid="workspace-launch-prefill-publish-button">
                <Play className="w-4 h-4" /> Preparar salida
              </button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-launch-metric-progress">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Progreso</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{launchPack.progress}%</p>
              <p className="mt-2 text-xs text-text-muted">Fase actual: {formatLaunchLabel(launchPack.stage)}</p>
            </div>
            <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-launch-metric-quality">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Quality score</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{launchPack.quality_score || 0}</p>
              <p className="mt-2 text-xs text-text-muted">Listo para aprobación: {launchPack.ready_for_approval ? 'sí' : 'todavía no'}</p>
            </div>
            <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-launch-metric-channels">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Canales</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{launchPack.platforms.length}</p>
              <p className="mt-2 text-xs text-text-muted">{launchPack.platforms.join(', ')}</p>
            </div>
            <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-launch-metric-agents">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Agentes completados</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{launchPack.completed_agents}/{launchPack.total_agents}</p>
              <p className="mt-2 text-xs text-text-muted">Tablero: {launchPack.board_summary.total_cards} tarjetas activas</p>
            </div>
          </div>

          {!!launchPack.blockers?.length && (
            <div className="mt-6 rounded-[24px] border border-amber-400/20 bg-amber-500/8 p-5" data-testid="workspace-launch-blockers-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300">Qué falta cerrar</p>
              <div className="mt-3 space-y-2" data-testid="workspace-launch-blockers-list">
                {launchPack.blockers.map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-text-body">
                    <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 grid gap-4 xl:grid-cols-2">
            <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="workspace-launch-strategy-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Posicionamiento</p>
              <p className="mt-3 text-sm leading-relaxed text-text-body">{launchPack.strategy_excerpt || 'Aún no hay resumen estratégico disponible.'}</p>
            </div>
            <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="workspace-launch-sales-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Ángulos de venta</p>
              <p className="mt-3 text-sm leading-relaxed text-text-body">{launchPack.sales_excerpt || launchPack.content_excerpt || 'Aún no hay extracto comercial visible.'}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="workspace-launch-next-steps-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Siguientes pasos</p>
              <div className="mt-4 space-y-3" data-testid="workspace-launch-next-steps-list">
                {launchPack.next_steps.map((step, index) => (
                  <div key={step} className="flex items-start gap-3 text-sm text-text-body">
                    <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{index + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="workspace-launch-board-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Tablero de lanzamiento</p>
                  <p className="mt-2 text-sm text-text-body">En progreso: {launchPack.board_summary.in_progress} · En revisión: {launchPack.board_summary.in_review} · Backlog: {launchPack.board_summary.backlog}</p>
                </div>
              </div>
              <div className="mt-4 space-y-3" data-testid="workspace-launch-board-list">
                {launchPack.board_cards.map((card) => (
                  <div key={card.id} className="rounded-[18px] border border-border-subtle bg-background/55 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-text-heading">{card.title}</p>
                        <p className="mt-1 text-xs text-text-muted">{formatLaunchLabel(card.lane)} · {card.owner} · {formatLaunchLabel(card.priority)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={actionBuilderRef} className="glass-card p-6" data-testid="workspace-action-builder">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Acción operativa</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">
              {activeAction === 'content' && 'Crear campaña'}
              {activeAction === 'video' && 'Generar vídeo corto'}
              {activeAction === 'publish' && 'Preparar publicación'}
              {activeAction === 'agents' && 'Lanzar agentes'}
            </h4>
          </div>
          <button onClick={submitWorkspaceAction} disabled={submitting} className="btn-primary" data-testid="workspace-submit-action-button">
            <Wand2 className={`w-4 h-4 ${submitting ? 'animate-spin' : ''}`} />
            {submitting ? 'Procesando...' : 'Ejecutar acción'}
          </button>
        </div>

        <div className="mt-5">
          {activeAction === 'content' && (
            <div className="grid gap-4 md:grid-cols-2" data-testid="workspace-form-content">
              <input className="input-kinesthetic" placeholder="Nombre de campaña" value={campaignForm.campaign_name} onChange={(e) => setCampaignForm((prev) => ({ ...prev, campaign_name: e.target.value }))} data-testid="workspace-campaign-name-input" />
              <input className="input-kinesthetic" placeholder="Audiencia" value={campaignForm.audience} onChange={(e) => setCampaignForm((prev) => ({ ...prev, audience: e.target.value }))} data-testid="workspace-campaign-audience-input" />
              <textarea className="input-kinesthetic resize-none md:col-span-2" rows={3} placeholder="Objetivo de negocio" value={campaignForm.business_goal} onChange={(e) => setCampaignForm((prev) => ({ ...prev, business_goal: e.target.value }))} data-testid="workspace-campaign-goal-input" />
              <input className="input-kinesthetic" placeholder="Voz de marca" value={campaignForm.brand_voice} onChange={(e) => setCampaignForm((prev) => ({ ...prev, brand_voice: e.target.value }))} data-testid="workspace-campaign-voice-input" />
              <input className="input-kinesthetic" placeholder="Oferta principal" value={campaignForm.offers} onChange={(e) => setCampaignForm((prev) => ({ ...prev, offers: e.target.value }))} data-testid="workspace-campaign-offer-input" />
              <input className="input-kinesthetic" placeholder="Plataformas separadas por coma" value={campaignForm.platforms} onChange={(e) => setCampaignForm((prev) => ({ ...prev, platforms: e.target.value }))} data-testid="workspace-campaign-platforms-input" />
              <input className="input-kinesthetic" type="number" min="1" max="12" placeholder="Semanas" value={campaignForm.weeks} onChange={(e) => setCampaignForm((prev) => ({ ...prev, weeks: e.target.value }))} data-testid="workspace-campaign-weeks-input" />
              <input className="input-kinesthetic" type="number" min="1" max="14" placeholder="Posts por semana" value={campaignForm.posts_per_week} onChange={(e) => setCampaignForm((prev) => ({ ...prev, posts_per_week: e.target.value }))} data-testid="workspace-campaign-posts-input" />
            </div>
          )}

          {activeAction === 'video' && (
            <div className="grid gap-4 md:grid-cols-2" data-testid="workspace-form-video">
              <input className="input-kinesthetic" placeholder="Título del vídeo" value={videoForm.title} onChange={(e) => setVideoForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="workspace-video-title-input" />
              <input className="input-kinesthetic" placeholder="Audiencia" value={videoForm.audience} onChange={(e) => setVideoForm((prev) => ({ ...prev, audience: e.target.value }))} data-testid="workspace-video-audience-input" />
              <textarea className="input-kinesthetic resize-none md:col-span-2" rows={3} placeholder="Objetivo" value={videoForm.objective} onChange={(e) => setVideoForm((prev) => ({ ...prev, objective: e.target.value }))} data-testid="workspace-video-objective-input" />
              <input className="input-kinesthetic" placeholder="Plataforma principal" value={videoForm.primary_platform} onChange={(e) => setVideoForm((prev) => ({ ...prev, primary_platform: e.target.value }))} data-testid="workspace-video-platform-input" />
              <input className="input-kinesthetic" type="number" min="15" max="180" placeholder="Duración en segundos" value={videoForm.duration_seconds} onChange={(e) => setVideoForm((prev) => ({ ...prev, duration_seconds: e.target.value }))} data-testid="workspace-video-duration-input" />
            </div>
          )}

          {activeAction === 'publish' && (
            <div className="grid gap-4 md:grid-cols-2" data-testid="workspace-form-publish">
              <input className="input-kinesthetic" placeholder="Nombre de campaña" value={publishForm.campaign_name} onChange={(e) => setPublishForm((prev) => ({ ...prev, campaign_name: e.target.value }))} data-testid="workspace-publish-name-input" />
              <input className="input-kinesthetic" placeholder="Canales separados por coma" value={publishForm.channels} onChange={(e) => setPublishForm((prev) => ({ ...prev, channels: e.target.value }))} data-testid="workspace-publish-channels-input" />
              <textarea className="input-kinesthetic resize-none md:col-span-2" rows={4} placeholder="Mensaje principal" value={publishForm.primary_message} onChange={(e) => setPublishForm((prev) => ({ ...prev, primary_message: e.target.value }))} data-testid="workspace-publish-message-input" />
              <input className="input-kinesthetic md:col-span-2" placeholder="Fecha/hora sugerida (opcional)" value={publishForm.scheduled_for} onChange={(e) => setPublishForm((prev) => ({ ...prev, scheduled_for: e.target.value }))} data-testid="workspace-publish-schedule-input" />
            </div>
          )}

          {activeAction === 'agents' && (
            <div className="grid gap-4 md:grid-cols-2" data-testid="workspace-form-agents">
              <input className="input-kinesthetic" placeholder="Título de tarea" value={taskForm.title} onChange={(e) => setTaskForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="workspace-task-title-input" />
              <input className="input-kinesthetic" placeholder="Agentes (ej: CONTENIDO,VENTAS)" value={taskForm.assigned_agents} onChange={(e) => setTaskForm((prev) => ({ ...prev, assigned_agents: e.target.value }))} data-testid="workspace-task-agents-input" />
              <textarea className="input-kinesthetic resize-none md:col-span-2" rows={4} placeholder="Descripción" value={taskForm.description} onChange={(e) => setTaskForm((prev) => ({ ...prev, description: e.target.value }))} data-testid="workspace-task-description-input" />
              <select className="input-kinesthetic md:col-span-2" value={taskForm.priority} onChange={(e) => setTaskForm((prev) => ({ ...prev, priority: e.target.value }))} data-testid="workspace-task-priority-select">
                <option value="baja">baja</option>
                <option value="media">media</option>
                <option value="alta">alta</option>
                <option value="critica">critica</option>
              </select>
            </div>
          )}
        </div>

        {actionResult && (
          <div className="mt-5 rounded-[22px] border border-primary/20 bg-primary/5 p-5" data-testid="workspace-action-result">
            <p className="text-[10px] uppercase tracking-[0.18em] text-primary">Resultado</p>
            {actionResult.order && <p className="mt-3 text-sm text-text-body">Campaña creada: <span className="text-text-heading font-semibold">{actionResult.order.campaign_name}</span>. Los agentes ya están trabajando en el borrador.</p>}
            {actionResult.video_brief && <div className="mt-3 whitespace-pre-wrap text-sm text-text-body">{actionResult.video_brief.result}</div>}
            {actionResult.publish_job && <p className="mt-3 text-sm text-text-body">Publicación preparada para: <span className="text-text-heading font-semibold">{actionResult.publish_job.channels.join(', ')}</span>. Estado: {actionResult.publish_job.status}.</p>}
            {actionResult.task && <p className="mt-3 text-sm text-text-body">Tarea lanzada: <span className="text-text-heading font-semibold">{actionResult.task.title}</span> con agentes {actionResult.task.assigned_agents.join(', ')}.</p>}
          </div>
        )}
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <div className="glass-card p-6" data-testid="workspace-onboarding-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Onboarding</p>
          <h4 className="mt-2 text-xl font-semibold text-text-heading">Checklist para arrancar</h4>
          <p className="mt-2 text-sm text-text-body">{workspace.onboarding.completed_steps}/{workspace.onboarding.total_steps} pasos completados</p>
          <div className="mt-5 space-y-3">
            {workspace.onboarding.steps.map((step) => (
              <div key={step.id} className={`rounded-[20px] border p-4 ${step.completed ? 'border-emerald-500/25 bg-emerald-500/8' : 'border-border-subtle bg-card/40'}`} data-testid={`workspace-onboarding-${step.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{step.label}</p>
                    <p className="mt-1 text-sm text-text-body">{step.description}</p>
                  </div>
                  {step.completed ? <CheckCircle2 className="w-5 h-5 text-emerald-300" /> : <Lock className="w-5 h-5 text-text-muted" />}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="workspace-connections-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Conexiones</p>
          <h4 className="mt-2 text-xl font-semibold text-text-heading">Todo listo para mañana</h4>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {workspace.connections.map((connection) => (
              <div key={connection.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`workspace-connection-${connection.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-text-heading">{connection.name}</p>
                  <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${connection.status === 'connected' ? 'bg-primary text-background' : 'bg-card border border-border-subtle text-text-muted'}`}>{connection.status}</span>
                </div>
                <p className="mt-2 text-sm text-text-body">{connection.helper}</p>
              </div>
            ))}
          </div>
          {workspace.upgrade_suggestion && (
            <div className="mt-5 rounded-[22px] border border-primary/20 bg-primary/5 p-4" data-testid="workspace-upgrade-suggestion">
              <p className="text-[10px] uppercase tracking-[0.18em] text-primary">Siguiente escalón sugerido</p>
              <p className="mt-2 text-lg font-semibold text-text-heading">{workspace.upgrade_suggestion.name}</p>
              <p className="mt-2 text-sm text-text-body">{workspace.upgrade_suggestion.reason}</p>
              <p className="mt-2 text-sm text-primary">Desde ${workspace.upgrade_suggestion.monthly_price} / mes</p>
            </div>
          )}
        </div>
      </div>

      <div className="glass-card p-6" data-testid="workspace-history-card">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Historial real</p>
        <h4 className="mt-2 text-xl font-semibold text-text-heading">Qué se ha creado dentro del Workspace</h4>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-history-campaigns">
            <p className="text-sm font-semibold text-text-heading">Campañas</p>
            <div className="mt-3 space-y-3">
              {(workspace.recent_history.campaigns || []).map((item) => <div key={item.id} className="text-sm text-text-body"><p className="font-medium text-text-heading">{item.campaign_name}</p><p className="text-xs text-text-muted">{item.status} · {(item.platforms || []).join(', ')}</p></div>)}
              {!(workspace.recent_history.campaigns || []).length && <p className="text-sm text-text-muted">Aún no hay campañas creadas.</p>}
            </div>
          </div>
          <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-history-videos">
            <p className="text-sm font-semibold text-text-heading">Vídeos</p>
            <div className="mt-3 space-y-3">
              {(workspace.recent_history.videos || []).map((item) => <div key={item.id} className="text-sm text-text-body"><p className="font-medium text-text-heading">{item.title}</p><p className="text-xs text-text-muted">{item.primary_platform} · {item.duration_seconds}s</p></div>)}
              {!(workspace.recent_history.videos || []).length && <p className="text-sm text-text-muted">Aún no hay vídeos generados.</p>}
            </div>
          </div>
          <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-history-publish">
            <p className="text-sm font-semibold text-text-heading">Publicaciones</p>
            <div className="mt-3 space-y-3">
              {(workspace.recent_history.publish_jobs || []).map((item) => <div key={item.id} className="text-sm text-text-body"><p className="font-medium text-text-heading">{item.campaign_name}</p><p className="text-xs text-text-muted">{item.status} · {(item.channels || []).join(', ')}</p></div>)}
              {!(workspace.recent_history.publish_jobs || []).length && <p className="text-sm text-text-muted">Aún no hay publicaciones en cola.</p>}
            </div>
          </div>
          <div className="rounded-[22px] border border-border-subtle bg-card/40 p-4" data-testid="workspace-history-tasks">
            <p className="text-sm font-semibold text-text-heading">Tareas con agentes</p>
            <div className="mt-3 space-y-3">
              {(workspace.recent_history.tasks || []).map((item) => <div key={item.id} className="text-sm text-text-body"><p className="font-medium text-text-heading">{item.title}</p><p className="text-xs text-text-muted">{item.status} · {(item.assigned_agents || []).join(', ')}</p></div>)}
              {!(workspace.recent_history.tasks || []).length && <p className="text-sm text-text-muted">Aún no hay tareas lanzadas.</p>}
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="workspace-modules-grid">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Módulos del workspace</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Capacidad por plan</h4>
          </div>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {workspace.modules.map((module) => {
            const Icon = MODULE_ICONS[module.id] || Sparkles;
            return (
              <motion.div key={module.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`rounded-[24px] border p-5 ${module.enabled ? 'border-primary/20 bg-primary/5' : 'border-border-subtle bg-card/40'}`} data-testid={`workspace-module-${module.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-border-subtle bg-background/60 text-primary"><Icon className="w-5 h-5" /></div>
                  <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.18em] ${module.enabled ? 'bg-primary text-background' : 'bg-card text-text-muted border border-border-subtle'}`}>{module.enabled ? 'activo' : 'bloqueado'}</span>
                </div>
                <h5 className="mt-4 text-lg font-semibold text-text-heading">{module.name}</h5>
                <p className="mt-2 text-sm text-text-body">{module.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {module.highlights.map((item) => (
                    <span key={item} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-text-muted">{item}</span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.82fr_1.18fr]">
        <div className="glass-card p-6" data-testid="workspace-security-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Seguridad activa</p>
          <h4 className="mt-2 text-xl font-semibold text-text-heading">Base fuerte para operar</h4>
          <div className="mt-4 rounded-[22px] border border-emerald-500/20 bg-emerald-500/8 p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">Security posture</p>
            <p className="mt-3 text-4xl font-bold text-text-heading">{workspace.security.security_score}/100</p>
          </div>
          <div className="mt-5 space-y-3">
            {securityHighlights.map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm text-text-body">
                <Shield className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="workspace-activity-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Actividad y pipeline</p>
          <h4 className="mt-2 text-xl font-semibold text-text-heading">Qué está listo para moverse</h4>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="space-y-3">
              {workspace.activity_feed.map((item) => (
                <div key={item.id} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`workspace-activity-${item.id}`}>
                  <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                  <p className="mt-2 text-sm text-text-body">{item.description}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {workspace.pipeline.map((item) => (
                <div key={item.label} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`workspace-pipeline-${item.label.toLowerCase()}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-text-body">{item.label}</p>
                    <p className="text-2xl font-bold text-primary">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}